import Link from 'next/link';
import ChevronIcon from '@/components/icons/chevron';
import type { SimilarDoctorsData } from '@/modules/profile/functions/getSimilarDoctorsInCity';

const rowClass =
  'flex items-center justify-between min-h-[44px] px-4 py-3 text-sm font-medium bg-white md:rounded-lg hover:text-primary';

/**
 * Server-rendered "same specialty, same city" links on the profile page.
 * Named doctors appear only when the profile allows recommending other doctors (decided server-side);
 * the listing link (e.g. «متخصصان جراحی مغز و اعصاب در تهران») is always shown, as a row like its neighbours.
 */
export const SimilarDoctorsLinks = ({ data }: { data?: SimilarDoctorsData | null }) => {
  if (!data?.listUrl || !data.label) return null;
  const doctors = data.doctors ?? [];

  const listingRow = (
    <Link href={data.listUrl} prefetch={false} className={rowClass}>
      <span>{data.label}</span>
      <ChevronIcon dir="left" />
    </Link>
  );

  if (!doctors.length) return <nav aria-label={data.label}>{listingRow}</nav>;

  const title = data.cityName ? `پزشکان مشابه در ${data.cityName}` : 'پزشکان مشابه';
  return (
    <nav aria-label={title} className="flex flex-col bg-white md:rounded-lg">
      <h2 className="px-4 pt-4 pb-2 text-sm font-bold">{title}</h2>
      <ul className="flex flex-col px-4">
        {doctors.map(doctor => (
          <li key={doctor.url} className="border-b border-slate-100">
            <Link href={doctor.url} prefetch={false} className="flex flex-col py-2 min-h-[44px] justify-center hover:text-primary">
              <span className="text-sm">{doctor.name}</span>
              {doctor.expertise && <span className="text-xs text-slate-500">{doctor.expertise}</span>}
            </Link>
          </li>
        ))}
      </ul>
      {listingRow}
    </nav>
  );
};

export default SimilarDoctorsLinks;
